import React, { useState, useEffect } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import {
  Button,
  SegmentedControl,
  Text,
  Box,
  LoadingOverlay,
  Flex,
  Title,
} from "@mantine/core";
import dayjs from "dayjs";

import { DateField } from "@components/shared";
import { formatUserCounts, getTimezoneOffsetFromName } from "./helpers";
import { TimeSlots, UserCounts } from "./";

export default function SmsSchedule({
  reqData,
  onSuccess,
  id,
  isStart,
  timezone,
}) {
  const initialFormValues = {
    start_date: "",
    start_time: "",
    timezone: "",
  };
  const [loading, setLoading] = useState(false);
  const [formValues, setFormValues] = useState(initialFormValues);
  const [method, setMethod] = useState("later");
  const [userTime, setUserTime] = useState(false);
  const [setupLoading, setSetupLoading] = useState(true);
  const [setupData, setSetupData] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);

  const timezoneOffset = getTimezoneOffsetFromName(timezone);

  useEffect(() => {
    fetchSetupData();

    // Set up polling every 15 seconds
    const interval = setInterval(() => {
      fetchSetupData();
    }, 15000);

    // Clean up interval on unmount
    return () => clearInterval(interval);
  }, [formValues.start_date, method]);
  // }, [userTime, method, formValues.timezone]);

  useEffect(() => {
    if (timezone) {
      setFormValues({
        ...formValues,
        timezone,
      });
    }
  }, [timezone]);

  function onClose() {
    setFormValues(initialFormValues);
    setLoading(false);
    setMethod("now");
    setSetupLoading(false);
    setSetupData(null);
  }

  function onClick() {
    setLoading(true);
    const req = {
      ...reqData,
      ...formValues,
      start_time: selectedTime,
    };

    axios
      .post(`/bulk-communicators/${id}/startup/`, req)
      .then(() => {
        setLoading(false);
        toast.success("Scheduled!");
        onClose();
        onSuccess();
      })
      .catch((err) => {
        setLoading(false);
        toast.error(err);
      });
  }

  function onStartClick() {
    setLoading(true);
    const req = {
      ...reqData,
      send_now: true,
    };

    axios
      .post(`/bulk-communicators/${id}/startup/`, req)
      .then(() => {
        setLoading(false);
        toast.success("Scheduled!");
        onClose();
        onSuccess();
      })
      .catch((err) => {
        setLoading(false);
        toast.error(err);
      });
  }

  function fetchSetupData() {
    setSetupLoading(true);

    const req = {
      bulk_communicator_id: id,
      timeframe: method,
      user_time: true,
      // timezone: formValues.timezone,
      context: "scheduler",
    };

    axios
      .post(`/bulk-communicators/${id}/data-grabber/`, req)
      .then(({ data }) => {
        const res = data.response[0];
        if (!res) {
          return setSetupLoading(false);
        }
        setSetupData(res);
        setFormValues({
          ...formValues,
          start_date:
            method === "later" && formValues.start_date
              ? formValues.start_date
              : res.date,
        });
        setSetupLoading(false);
        // make sure selected time is valid
        if (method === "later" && selectedTime && res.start_hours) {
          const selectedTimeMinutes =
            parseInt(selectedTime.split(":")[0]) * 60 +
            parseInt(selectedTime.split(":")[1]);
          const startHoursMinutes =
            parseInt(res.start_hours.split(":")[0]) * 60 +
            parseInt(res.start_hours.split(":")[1]);

          if (selectedTimeMinutes > startHoursMinutes) {
            setSelectedTime(null);
          }
        }
      })
      .catch((err) => {
        setSetupLoading(false);
        setSetupData(null);
      });
  }

  return (
    <>
      <div style={{ position: "relative" }}>
        <LoadingOverlay visible={setupLoading} />
        <Flex justify="center">
          <SegmentedControl
            mb="lg"
            size="sm"
            value={method}
            onChange={(e) => {
              setSelectedTime(null);
              setMethod(e);
            }}
            radius="xl"
            color="dark"
            data={[
              { label: "Send Now", value: "now" },
              // { label: "Send Today", value: "today" },
              { label: "Send Later", value: "later" },
            ]}
          />
        </Flex>
        {method === "now" && (
          <>
            <SendNow
              setupData={setupData}
              loading={loading}
              onClick={() => (isStart ? onStartClick() : onClick())}
            />
          </>
        )}
        {["later", "today"].includes(method) && (
          <>
            {selectedTime ? (
              <>
                <div>
                  <Title order={1} align="center">
                    {dayjs(formValues.start_date).format("MM/DD/YYYY")}{" "}
                    {dayjs()
                      .hour(parseInt(selectedTime.split(":")[0]))
                      .minute(parseInt(selectedTime.split(":")[1]))
                      .format("h:mm A")}
                  </Title>
                  <Flex justify="center">
                    <Button
                      size="xs"
                      variant="subtle"
                      color="gray"
                      onClick={() => setSelectedTime(null)}
                    >
                      Select a different time
                    </Button>
                  </Flex>
                  <Box mt="xl">
                    <SelectedTimePreview
                      time={selectedTime}
                      date={formValues.start_date}
                      userCounts={setupData?.user_counts}
                    />
                  </Box>
                  <Button
                    size="md"
                    onClick={onClick}
                    loading={loading}
                    color="green"
                    fullWidth
                    mt="lg"
                  >
                    Schedule It
                  </Button>
                </div>
              </>
            ) : (
              <>
                <Box
                // p="lg"
                // bg="var(--mantine-color-gray-1)"
                // style={{
                //   borderRadius: "var(--mantine-radius-md)",
                // }}
                >
                  <DateField
                    label=""
                    size="xl"
                    required
                    min={setupData?.date}
                    disabled={method === "today"}
                    value={formValues.start_date}
                    onChange={(e) => {
                      // Validate that the year is not before setupData.date's year
                      if (setupData?.date && e) {
                        const selectedYear = dayjs(e).year();
                        const setupDataYear = dayjs(setupData.date).year();

                        if (selectedYear < setupDataYear) {
                          // Don't update the form value if year is too early
                          return;
                        }
                      }

                      setFormValues({
                        ...formValues,
                        start_date: e,
                      });
                    }}
                  />
                </Box>
                <Box mt="md" mb="xl">
                  {/* <Text mb="xs" align="center" size="lg" fw={600}>
                    Select a time to send
                  </Text> */}
                  <TimeSlots
                    startTime={setupData?.start_hours}
                    endTime={setupData?.end_hours}
                    onSelect={(time) => setSelectedTime(time)}
                  />
                </Box>
              </>
            )}
          </>
        )}
      </div>
    </>
  );
}

const SelectedTimePreview = ({ time, date, userCounts = [] }) => {
  const formattedUserCounts = formatUserCounts({
    userCounts,
    date,
    time,
  });

  return (
    <div style={{ minHeight: "100px" }}>
      <Box>
        <UserCounts timezones={formattedUserCounts} />
      </Box>
    </div>
  );
};

const SendNow = ({ setupData, loading, onClick }) => {
  if (!setupData) return null;

  const userCounts = formatUserCounts({
    userCounts: setupData.user_counts,
    date: dayjs(new Date()).format("YYYY-MM-DD"),
    time: dayjs(setupData.send_time_utc).format("HH:mm"),
    type: "sms",
    timezoneOffset: new Date().getTimezoneOffset() / -60,
    sendAtUserTime: false,
  });

  const unavailableUserCounts = userCounts.filter(
    (userCount) => !userCount.available
  );
  const totalUnavailable = unavailableUserCounts.reduce(
    (acc, userCount) => acc + userCount.count,
    0
  );

  const canSend =
    totalUnavailable === 0 && setupData.send_time_utc && setupData.available;

  return (
    <>
      {canSend && (
        <Box mb="xl">
          <Text align="center" size="xl">
            The next available send time is:
          </Text>
          {setupData.send_time_utc && (
            <Title order={1} align="center">
              {dayjs(setupData.send_time_utc).format("MM/DD/YYYY hh:mm A")}
            </Title>
          )}
        </Box>
      )}
      {!canSend && (
        <Box mb="xl">
          <Title order={1} align="center">
            Send Now Unavailable
          </Title>
          <Text align="center" size="lg" c="red" fw={600}>
            ({totalUnavailable}) users spanning ({unavailableUserCounts.length})
            timezones will not receive the message.
          </Text>
        </Box>
      )}
      {setupData && (
        <>
          {setupData.user_counts.length > 0 && (
            <Box mt="md" mb="md">
              <UserCounts timezones={userCounts} />
            </Box>
          )}
          {canSend && (
            <Button
              mt="lg"
              onClick={onClick}
              loading={loading}
              color="green"
              size="md"
              fullWidth
            >
              Confirm
            </Button>
          )}
        </>
      )}
    </>
  );
};
